/**
 * Engagement progress — bars, savings trend, forgiving budget consistency.
 * @see frontend/src/design/DESIGN.md (progress panel pattern)
 */
import React from 'react';
import { CHART_RAMP } from '../../design/tokens';
import { rechartsTooltip, rechartsXAxis } from '../../design/chartDefaults';
import { savingsRateChartReady } from '../../utils/dashboardSectionGuards';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';

const EngagementProgressPanel = ({ progress, compact = false }) => {
  if (!progress) return null;

  const { visions = [], savings_rate_trend = [], budget_consistency = {} } = progress;
  const hasVision = visions.length > 0;
  const hasSavingsChart = savingsRateChartReady(savings_rate_trend);
  const showSavingsSection = !compact;
  const hasBudget = budget_consistency.consistency_pct != null;

  if (!hasVision && !hasSavingsChart && !hasBudget) {
    if (compact || !showSavingsSection) return null;
  }

  const visionRows = compact ? visions.slice(0, 1) : visions.slice(0, 4);
  const savingsData = savings_rate_trend.map((pt) => ({
    month: String(pt.month).slice(5),
    rate: pt.savings_rate_pct,
  }));

  return (
    <section className="surface-panel p-5 md:p-6">
      <h2 className="font-display text-h2 text-ink mb-6">Your progress</h2>

      {hasVision && (
        <div className={showSavingsSection || hasBudget ? 'pb-6 mb-6 border-b border-hairline' : ''}>
          <p className="text-label text-ink-muted mb-4">Vision goals</p>
          <ul className="space-y-4">
            {visionRows.map((v) => {
              const pct = Math.min(
                100,
                Math.max(
                  0,
                  ((Number(v.current_amount) || 0) / (Number(v.target_amount) || 1)) * 100
                )
              );
              return (
                <li key={v.vision_id || v.title}>
                  <div className="flex justify-between gap-3 mb-1.5">
                    <span className="text-body text-ink font-medium truncate">{v.title}</span>
                    <span className="figure text-body text-ink-muted shrink-0 tabular-nums">
                      {Math.round(pct)}%
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-hairline overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-400"
                      style={{ width: `${pct}%`, backgroundColor: CHART_RAMP[0] }}
                    />
                  </div>
                  <p className="text-label text-ink-muted mt-1.5 figure tabular-nums">
                    ${Number(v.current_amount || 0).toLocaleString()} / $
                    {Number(v.target_amount || 0).toLocaleString()}
                  </p>
                  {v.pace_message && (
                    <p className="text-label text-ink-muted mt-1">{v.pace_message}</p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {showSavingsSection && (
        <div className={hasBudget ? 'pb-6 mb-6 border-b border-hairline' : ''}>
          <p className="text-label text-ink-muted mb-1">Savings rate</p>
          {hasSavingsChart ? (
            <>
              <p className="text-label text-ink-muted mb-3">Surplus as % of income</p>
              <div className="h-24 w-full overflow-hidden">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={savingsData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <XAxis dataKey="month" {...rechartsXAxis} />
                    <YAxis hide width={0} tick={false} axisLine={false} domain={[0, 'auto']} />
                    <Tooltip
                      formatter={(v) => [`${v}%`, 'Savings rate']}
                      {...rechartsTooltip}
                    />
                    <Line
                      type="monotone"
                      dataKey="rate"
                      stroke={CHART_RAMP[0]}
                      strokeWidth={2}
                      dot={{ fill: CHART_RAMP[1], r: 3, strokeWidth: 0 }}
                      activeDot={{ fill: CHART_RAMP[0], r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <p className="text-body text-secondary mt-2">
              We&apos;ll chart your savings rate as months accumulate.
            </p>
          )}
        </div>
      )}

      {!compact && hasBudget && (
        <div>
          <p className="text-label text-ink-muted mb-2">Budget consistency</p>
          <p className="figure text-display text-pine mb-3 tabular-nums">
            {budget_consistency.consistency_pct}%
          </p>
          <p className="text-body text-ink-muted mb-3">Months on plan</p>
          <div className="flex flex-wrap gap-2">
            {(budget_consistency.months || []).map((m) => (
              <span
                key={m.month}
                className={`text-label px-2.5 py-1 rounded-full border ${
                  m.on_track
                    ? 'border-pine bg-pine-tint text-pine'
                    : 'border-hairline text-ink-faint'
                }`}
                title={m.note}
              >
                {String(m.month).slice(5)}
              </span>
            ))}
          </div>
          {budget_consistency.label && (
            <p className="text-label text-ink-muted mt-3">{budget_consistency.label}</p>
          )}
        </div>
      )}
    </section>
  );
};

export default EngagementProgressPanel;
